import { useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Cursor01Icon, Note01Icon, ViewIcon } from '@hugeicons/core-free-icons'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import { countryCodes } from '@/lib/constants/countries'
import { getCountryFlagEmoji } from '@/lib/utils/visitorAvatar'
import { VisitorOrb } from '@/components/dashboard/VisitorOrb'
import {
  activityKind,
  formatVisitDuration,
  isOnSite,
  type RealtimeActivity,
  type VisitorProfile,
} from '@/lib/realtime/visitorProfiles'
import { clickedPhrase } from '@/components/heatmaps/heatmapInsights'

const KIND = {
  view: { icon: ViewIcon, label: (e: RealtimeActivity) => `Viewed ${e.page || '/'}` },
  click: { icon: Cursor01Icon, label: (e: RealtimeActivity) => clickedPhrase(e.target, e.secondary) },
  form: { icon: Note01Icon, label: () => 'Submitted a form' },
} as const

export const countryName = (code?: string) => (code ? countryCodes[code] || code : '')

/** "12s ago", "4m ago", "2h ago" */
export function agoLabel(time: string, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - new Date(time).getTime()) / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  return `${Math.floor(m / 60)}h ago`
}

/** Re-render every `ms` so relative times and the on-site state stay honest. */
export function useNow(ms = 15000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms)
    return () => window.clearInterval(id)
  }, [ms])
  return now
}

export function VisitorAvatar({ profile, size = 32 }: { profile: Pick<VisitorProfile, 'key' | 'country' | 'region'>; size?: number }) {
  const flag = getCountryFlagEmoji(profile.country)
  return (
    <span className="relative shrink-0" style={{ width: size, height: size }} aria-hidden>
      <VisitorOrb visitorKey={profile.key} className="size-full" />
      {flag && (
        <span className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-card text-[11px] leading-none shadow-[0_0_0_1px_rgb(0_0_0/0.08)]">
          {flag}
        </span>
      )}
    </span>
  )
}

export function OnSitePill({ profile, now }: { profile: VisitorProfile; now: number }) {
  const live = isOnSite(profile, now)
  return (
    <span className="inline-flex h-5 shrink-0 items-center gap-[5px] rounded-md bg-muted px-1.5 text-xs font-medium leading-4 text-foreground">
      <span className={cn('size-1.5 rounded-full', live ? 'bg-live' : 'bg-foreground/25')} />
      {live ? 'On site now' : `Left ${agoLabel(profile.lastSeen, now)}`}
    </span>
  )
}

/**
 * One visit, read from the live feed: who (place, device, source), where they
 * landed and are now, and every page view, click and form submission in order.
 */
export function VisitorProfileSheet({
  profile,
  onOpenChange,
}: {
  profile: VisitorProfile | null
  onOpenChange: (open: boolean) => void
}) {
  const now = useNow()
  const place = profile ? [profile.region, countryName(profile.country)].filter(Boolean).join(', ') : ''
  const facts: [string, string][] = profile
    ? [
        ['Source', profile.source ? `${profile.source}${profile.medium ? ` · ${profile.medium}` : ''}` : 'Direct or unknown'],
        ['Device', profile.device || profile.deviceGroup || 'Unknown'],
        ['Landed on', profile.landingPage],
        ['Now on', profile.currentPage],
        ['Time on site', formatVisitDuration(profile.durationSeconds)],
        ['Started', new Date(profile.firstSeen).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })],
      ]
    : []

  return (
    <Sheet open={!!profile} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]">
        {profile && (
          <>
            <div className="flex flex-col gap-4 border-b border-border px-6 pb-5 pt-6">
              <div className="flex items-center gap-3 pr-8">
                <VisitorAvatar profile={profile} size={44} />
                <div className="flex min-w-0 flex-col gap-1">
                  <SheetTitle className="truncate text-base font-semibold leading-5">
                    {profile.country ? `Visitor from ${countryName(profile.country)}` : 'Visitor'}
                  </SheetTitle>
                  <SheetDescription className="truncate text-xs leading-4 text-foreground/60">
                    {[place && place !== countryName(profile.country) ? place : null, profile.browser].filter(Boolean).join(' · ') || 'Anonymous visit'}
                  </SheetDescription>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <OnSitePill profile={profile} now={now} />
                <span className="text-xs leading-4 text-foreground/60 tabular-nums">
                  {profile.views} {profile.views === 1 ? 'page' : 'pages'} · {profile.clicks} {profile.clicks === 1 ? 'click' : 'clicks'} · {profile.forms} {profile.forms === 1 ? 'form' : 'forms'}
                </span>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <dl className="mx-6 mt-5 flex flex-col rounded-xl bg-surface px-4 py-1 shadow-[0_0_0_1px_var(--border)] [&>div+div]:border-t [&>div+div]:border-border">
                {facts.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between gap-4 py-2.5 text-[13px] leading-4">
                    <dt className="shrink-0 text-foreground/60">{label}</dt>
                    <dd className="truncate text-right font-medium tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex flex-col gap-3 px-6 pb-6 pt-6">
                <h3 className="text-[13px] font-semibold leading-4">Journey</h3>
                <ol className="relative flex flex-col">
                  {[...profile.events].reverse().map((event, i, all) => {
                    const kind = activityKind(event)
                    const { icon, label } = KIND[kind]
                    const latest = i === 0
                    return (
                      <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                        {i < all.length - 1 && (
                          <span className="absolute left-[13px] top-7 bottom-0 w-px bg-border" aria-hidden />
                        )}
                        <span
                          className={cn(
                            'relative flex size-[27px] shrink-0 items-center justify-center rounded-full',
                            latest && isOnSite(profile, now)
                              ? 'bg-foreground text-background'
                              : 'bg-card text-foreground/60 shadow-[0_0_0_1px_var(--input)]',
                          )}
                        >
                          <HugeiconsIcon icon={icon} size={13} />
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="truncate text-[13px] font-medium leading-4">{label(event)}</span>
                            <span className="shrink-0 text-xs leading-4 text-foreground/50 tabular-nums">{agoLabel(event.time, now)}</span>
                          </div>
                          {kind !== 'view' && (
                            <span className="truncate text-xs leading-4 text-foreground/60">on {event.page || '/'}</span>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ol>
              </div>
            </div>

            <p className="border-t border-border px-6 py-3.5 text-xs leading-[18px] text-foreground/50">
              Built from this visit's events in the live feed. Visitors stay anonymous.
            </p>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
